'use es6';
/*
 * Global Groups are a special type of layout.
 * Entries here should not be used in the Layout Editor.
 * Use Unified Layouts instead.
 */

/*
 * SHAPE
 *
  {
    isFetchingForWidgets: false,
    fetchErrorForWidgets: null,
    widgets: {
      order: [123, 124, 125, 126],
      widgets: {
        123: GlobalGroup,
        124: GlobalGroup,
        125: GlobalGroup,
        126: GlobalGroup,
      },
    },
  }
*/

import SeamlessImmutable from 'seamless-immutable';
import { combineReducers } from 'redux';
import { sortBy, without } from 'underscore';
import { UNINITIALIZED, PENDING, FAILED, SUCCEEDED } from 'DesignData/enums/RequestStatus';
import { isGlobalGroup } from 'DesignData/helpers/layouts';
import { GlobalGroup as GlobalGroupWidget } from 'DesignData/models/widgets';
import { GLOBAL_GROUPS_FETCH_FOR_WIDGETS, LAYOUT_CELL_MAKE_GLOBAL, LAYOUT_DELETE, LAYOUT_MOVE_RENAME, LAYOUT_CREATE_FROM_FILE, LAYOUT_WIDGET_RESTORE_PARENT, LAYOUT_PUBLISH } from 'DesignData/actions/ActionTypes';

const getLabel = widget => widget.label.split('/').pop();

const getId = widget => widget.id;

function getSortedOrder(widgets) {
  return sortBy(widgets, getLabel).map(getId);
}

function addWidget(state, widget) {
  if (!isGlobalGroup(widget)) {
    return state;
  }

  const {
    id,
    label,
    path,
    generatedTemplatePath,
    categoryId
  } = widget;
  const newWidget = new GlobalGroupWidget({
    id,
    label,
    path,
    generatedTemplatePath,
    categoryId
  });
  const widgets = state.widgets.set(id, newWidget);
  const widgetsByPath = state.widgetsByPath.set(generatedTemplatePath || path, newWidget);
  const order = getSortedOrder(widgets);
  return state.merge({
    order,
    widgets,
    widgetsByPath
  });
}
/*
 * widgets
 */


function globalGroups(state = SeamlessImmutable({
  order: [],
  // Widgets by id
  widgets: {},
  // Widgets by path - alias references to `widgets`
  widgetsByPath: {},
  fetchStatus: UNINITIALIZED,
  isInitialized: false
}), action) {
  switch (action.type) {
    case GLOBAL_GROUPS_FETCH_FOR_WIDGETS:
      {
        if (!(action.widgets || action.error)) {
          return state.set('fetchStatus', PENDING);
        }

        if (action.error) {
          return state.set('fetchStatus', FAILED);
        }

        const updatedState = state.merge({
          fetchStatus: SUCCEEDED,
          isInitialized: true
        });

        if (!action.widgets.length) {
          return updatedState;
        }

        const order = getSortedOrder(action.widgets);
        const widgets = {};
        const widgetsByPath = {};
        action.widgets.forEach(globalGroup => {
          const ggWidget = new GlobalGroupWidget(globalGroup);
          widgets[globalGroup.id] = ggWidget;
          const path = globalGroup.generatedTemplatePath || globalGroup.path;
          widgetsByPath[path] = ggWidget;
        });
        return updatedState.merge({
          order,
          widgets,
          widgetsByPath
        });
      }

    case LAYOUT_CELL_MAKE_GLOBAL:
      {
        const {
          globalGroup
        } = action;

        if (!globalGroup) {
          return state;
        }

        const newWidget = new GlobalGroupWidget(globalGroup);
        const widgets = state.widgets.set(newWidget.id, newWidget);
        const path = newWidget.generatedTemplatePath || newWidget.path;
        const widgetsByPath = state.widgetsByPath.set(path, newWidget);
        const order = getSortedOrder(widgets);
        return state.merge({
          order,
          widgets,
          widgetsByPath
        });
      }

    case LAYOUT_DELETE:
      {
        const {
          layoutId,
          deleted
        } = action;

        if (!deleted) {
          return state;
        }

        const {
          order,
          widgets
        } = state;
        const ggWidget = widgets[layoutId];

        if (!ggWidget) {
          // Return if deleted layout was not a global group.
          return state;
        }

        return state.merge({
          order: without(order, ggWidget.id),
          widgets: widgets.without(ggWidget.id),
          widgetsByPath: state.widgetsByPath.without(ggWidget.generatedTemplatePath || ggWidget.path)
        });
      }

    case LAYOUT_MOVE_RENAME:
    case LAYOUT_CREATE_FROM_FILE:
      {
        const {
          layout
        } = action;
        return addWidget(state, layout);
      }

    case LAYOUT_WIDGET_RESTORE_PARENT:
      {
        const {
          widgetType,
          parent: layout
        } = action;

        if (!(widgetType === 'global_group' && layout)) {
          return state;
        }

        return addWidget(state, layout);
      }

    case LAYOUT_PUBLISH:
      {
        const {
          layout,
          layoutId
        } = action;
        const widget = state.widgets[layoutId];
        if (!(layout && widget)) return state;
        return state.setIn(['widgets', layoutId, 'isAvailableForNewContent'], layout.isAvailableForNewContent);
      }

    default:
      return state;
  }
}
/*
 * Export
 */


export default combineReducers({
  widgets: globalGroups
});